<script setup>
import EventStageProgress from '@/components/EventStageProgress.vue'
import EventTracksBadge from '@/components/EventTracksBadge.vue'
import DateRange from '@/components/DateRange.vue'
import { eventStateColor } from '@/lib/events'
import { eventTypeStyle } from '@/lib/eventTypes'
import { podiumColor } from '@/lib/podium'
import { useEventTitle } from '@/composables/useEventTitle'

/**
 * Reusable grid of event cards.
 *
 * The card leads with what the event is (its name, on the colour of its type) and closes with the
 * one number a player looks for: their own result. Everything in between is one compact line, so the
 * cards keep the same height whatever they hold.
 *
 * It only renders the list it is given: loading, filtering and error states stay with the caller,
 * so the same component works on the events page and on a track page.
 */
defineProps({
  /** Events, in the shape returned by GET /api/events. */
  events: { type: Array, default: () => [] },
  /** Grid attributes for each card column — override them when the container is narrower. */
  cols: { type: [Number, String], default: 12 },
  md: { type: [Number, String], default: 6 },
  lg: { type: [Number, String], default: 4 },
})

const eventTitle = useEventTitle()

/** Tint of the card head, drawn from the colour of the event type. */
const headerStyle = event => ({
  background: `rgba(var(--v-theme-${eventTypeStyle(event.eventType).color}), 0.12)`,
})

/** The value of a player's own result: the points of a points event, the time otherwise. */
const resultValue = (entry, pointsBased) => {
  if (!entry) return ''

  return pointsBased && entry.points !== null ? entry.points : (entry.resultTime ?? '')
}
</script>

<template>
  <v-row>
    <v-col v-for="event in events" :key="event.id" :cols="cols" :md="md" :lg="lg">
      <v-card
        :to="{ name: 'event_detail', params: { id: event.id } }"
        hover
        elevation="3"
        rounded="lg"
        class="h-100 d-flex flex-column"
      >
        <div class="d-flex align-center ga-3 pa-3" :style="headerStyle(event)">
          <v-avatar :color="eventTypeStyle(event.eventType).color" size="38">
            <v-icon size="20">{{ eventTypeStyle(event.eventType).icon }}</v-icon>
          </v-avatar>

          <h3 class="text-subtitle-1 font-weight-bold flex-grow-1 text-truncate mb-0">
            {{ eventTitle(event) }}
          </h3>

          <v-chip :color="eventStateColor(event.state)" size="small" variant="flat" label>
            {{ $t(`eventStates.${event.state}`) }}
          </v-chip>
        </div>

        <v-card-text class="flex-grow-1 pa-3">
          <p class="text-caption text-medium-emphasis d-flex align-center ga-1 mb-2">
            <v-icon size="14">mdi-calendar-range</v-icon>
            <DateRange :from="event.dateStart" :to="event.dateEnd" />
          </p>

          <div class="d-flex flex-wrap align-center ga-2">
            <v-chip
              v-if="event.allTracks"
              color="brown"
              size="small"
              variant="tonal"
              prepend-icon="mdi-earth"
            >
              {{ $t('events.allTracks') }}
            </v-chip>

            <EventTracksBadge v-else :tracks="event.tracks" />

            <v-chip v-if="event.usesLaps" size="small" variant="tonal" prepend-icon="mdi-counter">
              {{ $t('events.lapsCount', { n: event.defaultLaps }) }}
            </v-chip>
          </div>
        </v-card-text>

        <v-divider />

        <div class="pa-3 d-flex flex-column ga-2">
          <div v-if="event.myEntry" class="d-flex align-center ga-2">
            <v-icon color="primary" size="18">mdi-account-check</v-icon>
            <span class="text-caption text-medium-emphasis flex-grow-1">{{ $t('events.yourResult') }}</span>
            <v-chip
              :color="podiumColor(event.myEntry.position) ?? 'primary'"
              size="small"
              variant="flat"
              label
            >
              {{ $t('events.position') }} {{ event.myEntry.position }}
            </v-chip>
            <span class="text-subtitle-1 font-weight-bold">
              {{ resultValue(event.myEntry, event.pointsBased) }}
              <span v-if="event.pointsBased && event.myEntry.points !== null" class="text-caption font-weight-regular">
                {{ $t('events.points') }}
              </span>
            </span>
          </div>

          <div v-else-if="event.playable" class="d-flex align-center ga-2 text-medium-emphasis">
            <v-icon size="18">mdi-clock-outline</v-icon>
            <span class="text-caption flex-grow-1">{{ $t('events.todo') }}</span>
            <span class="text-body-2 font-weight-medium text-primary">{{ $t('events.participate') }}</span>
            <v-icon size="18">mdi-chevron-right</v-icon>
          </div>

          <div v-else class="d-flex align-center ga-2 text-medium-emphasis">
            <v-icon size="18">{{ event.state === 'scheduled' ? 'mdi-hourglass-empty' : 'mdi-flag-checkered' }}</v-icon>
            <span class="text-caption flex-grow-1">
              {{ event.state === 'scheduled' ? $t('events.notPlayable') : $t('events.finished') }}
            </span>
            <v-icon size="18">mdi-chevron-right</v-icon>
          </div>

          <!-- A stage event still to be raced: how far the player is through its tracks -->
          <EventStageProgress
            v-if="event.stagesLeft !== null && event.state !== 'finished'"
            :stages-left="event.stagesLeft"
            :total="event.tracks.length"
          />
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>
